import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AccountProfileMenu } from "@/components/Layout/account/AccountProfileMenu";
import { AccountProjectSelector } from "@/components/Layout/account/AccountProjectSelector";
import { holdsPermissions } from "@/components/Layout/account/sidebarPermissions";
import {
  BILLING_PAGE_COMPONENT_ID,
  getAccountComponentRoute,
  ROUTES,
} from "@/constants/navigation";
import { useRealmContext } from "@/contexts/RealmContext";
import { useUser } from "@/contexts/UserContext";
import { useBillingAccount } from "@/hooks/useBillingAccount";
import "./AccountHeader.css";

// The permission the balance is read under, named the way the introspection
// endpoint names it -- the same name the billing page's menu entry is shown by.
const BILLING_ACCOUNT_READ = "exordos_ecosystem.billing_account.read";

export function AccountHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeRealm, realmLogout } = useRealmContext();
  const { session } = useUser();
  // The balance of the account this project spends from, written the way the
  // platform writes it. It is asked for only by an account that may read one:
  // the request would come back refused otherwise, and a refusal is reported
  // to the visitor, who did nothing and can do nothing about it.
  const { account } = useBillingAccount({
    enabled: Boolean(session.projectId)
      && holdsPermissions([BILLING_ACCOUNT_READ], session.permissions),
    projectId: session.projectId,
  });

  // An account signed into an installation reads it beside its own name: the
  // installation it is working in, and the way back to the central platform.
  const handleBackToCentral = () => {
    realmLogout();
    navigate(ROUTES.account);
  };

  return (
    <header className="account-header" aria-label={t("accountLayout.headerLabel")}>
      <AccountProjectSelector />
      {account ? (
        // The figure is where a visitor looks for how to top it up, so it
        // opens the billing page.
        <Link
          aria-label={t("accountLayout.balanceLink", {
            balance: account.balanceDisplay,
          })}
          className="account-header__balance"
          to={getAccountComponentRoute(BILLING_PAGE_COMPONENT_ID)}
        >
          {account.balanceDisplay}
        </Link>
      ) : null}
      {activeRealm ? (
        <span className="account-header__realm">
          <span className="account-header__realm-name" title={activeRealm.domain}>
            {activeRealm.name}
          </span>
          <button
            className="account-header__realm-exit"
            onClick={handleBackToCentral}
            type="button"
          >
            {t("accountLayout.backToCentral")}
          </button>
        </span>
      ) : null}
      <AccountProfileMenu />
    </header>
  );
}
